/**
 * Q6 — "What's behind the score?" (SYR-68, Phase C).
 *
 * The eight building blocks, exactly as the /dashboard shows them: filtered
 * latent elements with their customer-facing question + the Building Blocks AI
 * description (fetched from the same `/building-blocks-ai/insights` endpoint).
 * Left-border colour by band.
 */

import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { LockOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import {
  useGetBuildingBlocksInsightsMutation,
} from '../../../services/nestjs-base-api';
import type { IBrandEquityScoreResponse } from '../../../services/nestjs-base-api';
import { getDisplayName } from '../../../utils/elementDisplayNames';
import { useScopedNavigate } from '../useScopedNavigate';
import { MARKETER_UPGRADE_ROUTE } from '../gating/featureMatrix';
import { SectionHead } from './SectionHead';
import {
  BAND_PALETTE,
  bandFromClassification,
  bandFromScore,
  formatBipolarScore,
} from '../config/bandColours';
import { colourForScore } from '../config/elementColours';
import type { Band, ScoreNode } from '../types';

export interface BuildingBlocksProps {
  response: IBrandEquityScoreResponse;
  brandId: number;
  /** Open the shared element drawer for a clicked block. */
  onOpen: (node: ScoreNode) => void;
  /** canonical element name → ScoreNode, for resolving the clicked block. */
  nodeByCanonical: Map<string, ScoreNode>;
}

// Customer-facing questions, keyed by canonical element name — mirrors
// DashboardPage's `coreQuestions` so Q6 matches the dashboard exactly.
const CORE_QUESTIONS: Record<string, string> = {
  'Willingness to Pay a Premium': 'Could the brand charge more?',
  'Behavioural Loyalty': 'Are customers likely to buy again?',
  'Attitudinal Loyalty': 'Are customers proud to choose this brand?',
  'Market Awareness': 'Is the brand front of mind?',
  Associations: 'Does the brand stand out in a positive way?',
  'Brand Attachment': 'Are customers emotionally attached to the brand?',
  'Quality Value Tradeoff (PQV)': 'Does the brand feel worth its price?',
  'Alignment Depth': 'Do brand touchpoints deliver a great experience?',
};

const BLOCK_ORDER = [
  'Willingness to Pay a Premium',
  'Behavioural Loyalty',
  'Attitudinal Loyalty',
  'Market Awareness',
  'Associations',
  'Brand Attachment',
  'Quality Value Tradeoff (PQV)',
  'Alignment Depth',
];

const EXCLUDED = new Set(['Brand Equity', 'Brand Loyalty', 'Perceived Quality']);

interface InsightResult {
  response: string | null;
  cached: boolean;
  elementKey: string;
}

export const BuildingBlocks: React.FunctionComponent<BuildingBlocksProps> = ({
  response,
  brandId,
  onOpen,
  nodeByCanonical,
}) => {
  const { user } = useAuth();
  const navigate = useScopedNavigate();
  const [getInsights] = useGetBuildingBlocksInsightsMutation();
  const [insights, setInsights] = useState<Record<string, InsightResult> | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!user?.jwtToken || !brandId || !response) return;
    getInsights({ jwtToken: user.jwtToken, brandEquityData: response, brandId })
      .unwrap()
      .then((res) => {
        if (!cancelled) setInsights(res.insights as Record<string, InsightResult>);
      })
      .catch(() => {
        if (!cancelled) setInsights(null); // descriptions are best-effort
      });
    return () => {
      cancelled = true;
    };
  }, [user?.jwtToken, brandId, response, getInsights]);

  const blocks = response.latentElementScores
    .filter((e) => !EXCLUDED.has(e.element) && e.element in CORE_QUESTIONS)
    .sort(
      (a, b) =>
        (BLOCK_ORDER.indexOf(a.element) + 1 || 99) -
        (BLOCK_ORDER.indexOf(b.element) + 1 || 99),
    );

  if (blocks.length === 0) return null;

  return (
    <section id="behind" style={{ paddingTop: 44 }}>
      <SectionHead
        q="Q6"
        title={
          <FormattedMessage
            id="marketerView.q6.title"
            defaultMessage="What's behind the score?"
          />
        }
        sub={
          <FormattedMessage
            id="marketerView.q6.sub"
            defaultMessage="Eight building blocks sit beneath the headline score: each one a question your customers answered. Together they're what pushes the number up, and what holds it back."
          />
        }
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        {blocks.map((el) => {
          const isLocked = !!el.locked;
          const hasScore = el.score != null && !Number.isNaN(el.score);
          const band: Band = !hasScore
            ? 'na'
            : el.classificationLabel
              ? bandFromClassification(el.classificationLabel)
              : bandFromScore(el.score);
          const palette = BAND_PALETTE[band];
          const scoreColour = hasScore ? colourForScore(el.element, el.score) : palette.fg;
          const desc = insights?.[el.element]?.response?.replace(/\*\*/g, '') ?? '';
          const node = nodeByCanonical.get(el.element);
          // Locked cards don't open the drawer (its content is locked too).
          const open = node && !isLocked ? () => onOpen(node) : undefined;
          return (
            <div
              key={el.element}
              role={open ? 'button' : undefined}
              tabIndex={open ? 0 : undefined}
              onClick={open}
              onKeyDown={
                open
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        open();
                      }
                    }
                  : undefined
              }
              style={{
                background: '#fff',
                border: '1px solid #F1F5F9',
                borderLeft: `4px solid ${scoreColour}`,
                borderRadius: 14,
                padding: '18px 20px',
                cursor: open ? 'pointer' : 'default',
                transition: 'box-shadow 0.15s, transform 0.15s',
              }}
              onMouseEnter={
                open
                  ? (e) => {
                      e.currentTarget.style.boxShadow =
                        '0 8px 24px -10px rgba(15,23,42,0.25)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  : undefined
              }
              onMouseLeave={
                open
                  ? (e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'none';
                    }
                  : undefined
              }
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 12,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: '#64748B',
                  }}
                >
                  {getDisplayName(el.element)}
                </span>
                {!isLocked && (
                  <span style={{ fontSize: 18, fontWeight: 800, color: scoreColour }}>
                    {hasScore ? formatBipolarScore(el.score) : 'N/A'}
                  </span>
                )}
              </div>
              <h4
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: isLocked ? '#64748B' : '#0F172A',
                  margin: '8px 0 0',
                }}
              >
                {CORE_QUESTIONS[el.element]}
              </h4>
              {isLocked ? (
                <div style={{ marginTop: 10 }}>
                  {/* Greyed placeholder bars + upgrade teaser — mirrors the
                      dashboard's locked building-block treatment. */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                    <div style={{ height: 10, borderRadius: 4, background: '#E7E5E4', width: '100%' }} />
                    <div style={{ height: 10, borderRadius: 4, background: '#E7E5E4', width: '75%' }} />
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(MARKETER_UPGRADE_ROUTE);
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#F59E0B',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    <LockOutlined style={{ fontSize: 11 }} />
                    <FormattedMessage
                      id="marketerView.lock.unlockInsight"
                      defaultMessage="Upgrade to unlock insight"
                    />
                  </button>
                </div>
              ) : (
                desc && (
                  <p
                    style={{
                      fontSize: 14,
                      lineHeight: 1.55,
                      color: '#64748B',
                      margin: '8px 0 0',
                    }}
                  >
                    {desc}
                  </p>
                )
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
